import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  UsersFour, MagnifyingGlass, CheckCircle, ArrowLeft, User, ShieldCheck, X,
  Warning, QrCode, DeviceMobile, Copy, WifiSlash, CloudArrowUp, ArrowClockwise,
  Barcode,
} from "@phosphor-icons/react";
import { api, apiError, subscribeQueue, drainQueue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SignaturePad from "@/components/SignaturePad";
import SelfieCapture from "@/components/SelfieCapture";

/**
 * Modo Kiosco — dispositivo compartido en bocamina. Un supervisor loggea
 * su cuenta y deja el tablet apoyado; cada trabajador busca su nombre,
 * dibuja su firma y sale para dar paso al siguiente. La firma se guarda
 * inline en la asistencia del día con el hash de auditoría propio del kiosco.
 */
export default function KioskSignatures() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [signature, setSignature] = useState("");
  const [selfie, setSelfie] = useState("");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [qrSess, setQrSess] = useState(null);   // { token, mobile_url, expires_at }
  const [qrLoading, setQrLoading] = useState(false);
  const [selfieRequired, setSelfieRequired] = useState(false);
  const [selfieReason, setSelfieReason] = useState("");
  // Iter133 · input de tarjeta RFID/barcode. Los lectores USB emiten los
  // dígitos como teclado + Enter — enfocamos siempre este campo cuando
  // no hay operador seleccionado para que el escaneo funcione sin
  // interacción manual.
  const [rfidBuf, setRfidBuf] = useState("");
  const [rfidLookingUp, setRfidLookingUp] = useState(false);
  const rfidRef = useRef(null);
  // Iter102: indicador offline + cola de firmas pendientes (IndexedDB)
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [queueSummary, setQueueSummary] = useState({ pending: 0, failed: 0, total: 0 });
  const [syncing, setSyncing] = useState(false);
  const inputRef = useRef(null);

  const canOpen = user && ["admin", "manager", "supervisor"].includes(user.role);

  // Iter102: mantiene el estado online + suscribe la cola offline.
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const unsub = subscribeQueue(setQueueSummary);
    const onSynced = (e) => {
      const synced = e?.detail?.synced || 0;
      if (synced > 0) toast.success(`✅ ${synced} firma(s) sincronizada(s) con el servidor`);
    };
    window.addEventListener("offline-queue-synced", onSynced);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("offline-queue-synced", onSynced);
      unsub();
    };
  }, []);

  const forceSync = useCallback(async () => {
    if (!navigator.onLine) { toast.warning("Sin conexión — no se puede sincronizar aún"); return; }
    setSyncing(true);
    try {
      const r = await drainQueue(api);
      if (r.synced > 0) toast.success(`✅ ${r.synced} firma(s) subida(s)`);
      else if (r.remaining === 0) toast.info("Nada pendiente para sincronizar");
      else toast.warning(`Quedan ${r.remaining} pendiente(s) — reintentar más tarde`);
    } finally { setSyncing(false); }
  }, []);

  useEffect(() => {
    if (!canOpen) return;
    api.get("/hr/employees").then((r) => setEmployees(r.data || [])).catch(() => setEmployees([]));
  }, [canOpen]);

  useEffect(() => { if (!picked && inputRef.current) inputRef.current.focus(); }, [picked]);

  // Iter133 · re-enfoca el input de RFID cuando el operador vuelve al
  // paso de selección (después de firmar). Esto permite que el siguiente
  // trabajador escanee su tarjeta sin tocar la pantalla.
  useEffect(() => {
    if (!picked && rfidRef.current) {
      const t = setTimeout(() => rfidRef.current?.focus(), 220);
      return () => clearTimeout(t);
    }
  }, [picked]);

  const lookupByCode = useCallback(async (raw) => {
    const code = (raw || "").trim();
    if (code.length < 2) return;
    setRfidLookingUp(true);
    try {
      const { data } = await api.get(`/hr/kiosk/lookup`, { params: { code } });
      if (data?.id) {
        setPicked(data);
        toast.success(`Tarjeta OK · ${data.name}`);
        setRfidBuf("");
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) toast.warning("Tarjeta/código sin match — busca por nombre");
      else toast.error(apiError(e));
    } finally { setRfidLookingUp(false); }
  }, []);

  useEffect(() => {
    if (!picked) { setSelfieRequired(false); setSelfieReason(""); return; }
    api.get(`/hr/employees/${picked.id}/selfie-required`)
      .then((r) => { setSelfieRequired(!!r.data?.required); setSelfieReason(r.data?.reason || ""); })
      .catch(() => { setSelfieRequired(false); setSelfieReason(""); });
  }, [picked]);

  const startQrSession = useCallback(async () => {
    setQrLoading(true);
    try {
      const { data } = await api.post("/hr/kiosk/sessions", { hours_valid: 2 });
      setQrSess(data);
      toast.success("QR generado · válido 2h");
    } catch (e) { toast.error(apiError(e)); }
    finally { setQrLoading(false); }
  }, []);

  const copyMobileUrl = useCallback(async () => {
    if (!qrSess?.mobile_url) return;
    try {
      await navigator.clipboard.writeText(qrSess.mobile_url);
      toast.success("Link copiado");
    } catch { toast.error("No se pudo copiar el link"); }
  }, [qrSess]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return employees.filter((e) => {
      if (e.status !== "active") return false;
      return (e.name || "").toLowerCase().includes(s)
        || (e.dni || "").toLowerCase().includes(s)
        || (e.crew || "").toLowerCase().includes(s);
    }).slice(0, 8);
  }, [employees, q]);

  const sign = useCallback(async () => {
    if (!picked || !signature) { toast.error("Elige trabajador y dibuja firma"); return; }
    if (selfieRequired && !selfie) {
      toast.error("Selfie obligatorio en la primera firma del mes"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/hr/kiosk/sign-attendance", {
        employee_id: picked.id,
        status: "present",
        signature_data_url: signature,
        selfie_data_url: selfie,
      });
      // Iter102: si estamos sin red, la firma quedó encolada en IndexedDB
      // y se enviará al servidor cuando vuelva la señal. Feedback claro.
      if (data?._offline) {
        toast.success(`📴 ${picked.name} firmó offline — se sincronizará al reconectar`, { duration: 5000 });
      } else {
        toast.success(`✔ ${data.name || picked.name} firmó`);
      }
      setRecent((prev) => [{
        name: data.name || picked.name,
        at: new Date().toLocaleTimeString(),
        hash: (data.audit_hash || "").slice(0, 12) || (data._offline ? "offline" : ""),
        offline: !!data._offline,
      }, ...prev].slice(0, 12));
      setPicked(null);
      setSignature("");
      setSelfie("");
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }, [picked, signature, selfie, selfieRequired]);

  if (!canOpen) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center max-w-md">
          <Warning size={44} weight="duotone" className="text-amber-400 mx-auto mb-3" />
          <h1 className="font-heading font-black text-xl mb-2">Kiosco solo para supervisores</h1>
          <p className="text-sm text-slate-300">
            Debes iniciar sesión con una cuenta de admin, manager o supervisor para activar el modo Kiosco.
          </p>
          <Button onClick={() => nav("/login")} className="mt-4 gap-1"><ArrowLeft size={14} weight="bold" /> Volver</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white p-4 md:p-8" data-testid="kiosk-page">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-blue-800 flex items-center justify-center">
              <UsersFour size={24} weight="fill" className="text-amber-300" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-blue-200">Modo Kiosco</p>
              <h1 className="font-heading font-black text-2xl leading-tight">Firma de asistencia</h1>
              <p className="text-[11px] text-slate-400">Operador: <b className="text-slate-200">{user.name}</b></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Iter102: indicador de conexión + cola offline */}
            {!online && (
              <span
                data-testid="kiosk-offline-indicator"
                className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1.5 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/50"
              >
                <WifiSlash size={14} weight="bold" /> Sin conexión
              </span>
            )}
            {queueSummary.total > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={forceSync}
                disabled={syncing || !online}
                data-testid="kiosk-sync-btn"
                className="bg-slate-900 border-amber-500 text-amber-200 hover:bg-slate-800 gap-1"
                title={`${queueSummary.pending} pendiente(s) · ${queueSummary.failed} fallida(s)`}
              >
                {syncing ? (
                  <ArrowClockwise size={14} weight="bold" className="animate-spin" />
                ) : (
                  <CloudArrowUp size={14} weight="bold" />
                )}
                {queueSummary.total} en cola
              </Button>
            )}
            <Button
              variant="outline" onClick={startQrSession} disabled={qrLoading || !online}
              data-testid="kiosk-qr-generate"
              className="bg-amber-400 hover:bg-amber-300 border-amber-500 text-slate-950 font-bold gap-1"
            >
              <QrCode size={16} weight="fill" /> {qrLoading ? "Generando…" : "Firma En App"}
            </Button>
            <Button variant="outline" onClick={() => nav("/hr")}
                    data-testid="kiosk-exit"
                    className="bg-transparent border-slate-700 text-white hover:bg-slate-800 gap-1">
              <ArrowLeft size={14} weight="bold" /> Salir del Kiosco
            </Button>
          </div>
        </div>

        {!online && (
          <div className="bg-amber-900/30 border border-amber-600/50 rounded-xl p-3 text-amber-100" data-testid="kiosk-offline-banner">
            <p className="text-xs">
              <b>Modo Offline:</b> las firmas se están guardando en este dispositivo y se
              enviarán automáticamente al servidor cuando vuelva la conexión.
              {queueSummary.pending > 0 && ` · ${queueSummary.pending} pendiente(s) en cola.`}
            </p>
          </div>
        )}

        {qrSess && (
          <div className="bg-slate-900 border border-amber-500 rounded-2xl p-5 shadow-xl" data-testid="kiosk-qr-panel">
            <div className="flex items-start gap-5 flex-wrap">
              <div className="bg-white p-3 rounded-lg shrink-0">
                <QRCodeSVG value={qrSess.mobile_url} size={168} level="M" data-testid="kiosk-qr-svg" />
              </div>
              <div className="flex-1 min-w-[240px]">
                <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300 flex items-center gap-1">
                  <DeviceMobile size={14} weight="fill" /> Firma desde tu celular
                </p>
                <p className="text-sm text-slate-200 mt-1">
                  Cada trabajador escanea el QR con la cámara de su móvil personal para firmar
                  su asistencia sin tocar este tablet. Ideal para higiene y trazabilidad forense.
                </p>
                <p className="text-[11px] text-slate-400 mt-2">
                  Válido hasta <b className="text-slate-200">{qrSess.expires_at?.slice(0, 16).replace("T", " ")} UTC</b> · Token <span className="font-mono text-amber-200">{qrSess.token}</span>
                </p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={copyMobileUrl}
                          data-testid="kiosk-qr-copy"
                          className="bg-slate-800 hover:bg-slate-700 gap-1 text-white">
                    <Copy size={12} weight="bold" /> Copiar link
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setQrSess(null)}
                          data-testid="kiosk-qr-close"
                          className="bg-transparent border-slate-700 text-white hover:bg-slate-800">
                    Cerrar QR
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main card */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
          {!picked ? (
            <>
              {/* Iter133 · Input dedicado a lectores RFID/Barcode.
                  Autofocus siempre — al escanear la tarjeta el lector
                  emite dígitos + Enter y disparamos el lookup. */}
              <div className="mb-4 bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/40 rounded-lg p-3">
                <label className="text-[10px] uppercase tracking-widest font-bold text-amber-200 mb-1.5 flex items-center gap-1.5">
                  <Barcode size={12} weight="fill" /> Escanea tarjeta o código de barras
                </label>
                <Input
                  ref={rfidRef}
                  value={rfidBuf}
                  onChange={(e) => setRfidBuf(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      lookupByCode(rfidBuf);
                    }
                  }}
                  placeholder={rfidLookingUp ? "Buscando…" : "Pasa tu tarjeta RFID o casco escaneable"}
                  className="h-11 bg-slate-950 border-amber-600/40 text-white font-mono tracking-wider placeholder:text-slate-500"
                  data-testid="kiosk-rfid-input"
                  disabled={rfidLookingUp}
                  autoFocus
                />
                <p className="text-[10px] text-amber-100/70 italic mt-1.5">
                  El lector emite dígitos + Enter automáticamente. Sino, escribe el número y presiona Enter.
                </p>
              </div>

              <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2 block">
                O busca tu nombre, DNI o cuadrilla
              </label>
              <div className="relative">
                <MagnifyingGlass size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ej: Juan Pérez, 12345678, cuadrilla A"
                  className="pl-10 h-14 text-lg bg-slate-950 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="kiosk-search"
                />
              </div>
              {filtered.length > 0 && (
                <ul className="mt-3 space-y-1" data-testid="kiosk-results">
                  {filtered.map((e) => (
                    <li key={e.id}>
                      <button
                        onClick={() => setPicked(e)}
                        data-testid={`kiosk-pick-${e.id}`}
                        className="w-full text-left px-4 py-3 rounded-lg bg-slate-800 hover:bg-blue-800 border border-slate-700 hover:border-blue-500 transition"
                      >
                        <div className="flex items-center gap-3">
                          <User size={20} weight="fill" className="text-slate-400" />
                          <div className="flex-1">
                            <p className="font-bold text-white">{e.name}</p>
                            <p className="text-[11px] text-slate-400">{e.position} · Cuadr. {e.crew || "-"} · DNI {e.dni || "-"}</p>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {q && filtered.length === 0 && (
                <p className="mt-3 text-sm text-slate-500 italic text-center">Sin coincidencias — revisa tu búsqueda.</p>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Firmando como</p>
                  <p className="font-heading font-black text-2xl" data-testid="kiosk-picked-name">{picked.name}</p>
                  <p className="text-xs text-slate-400">{picked.position} · Cuadrilla {picked.crew || "-"}</p>
                </div>
                <button
                  onClick={() => { setPicked(null); setSignature(""); }}
                  data-testid="kiosk-back"
                  className="text-slate-400 hover:text-white"
                  title="Cambiar de trabajador"
                >
                  <X size={22} weight="bold" />
                </button>
              </div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2 block">
                Dibuja tu firma aquí
              </label>
              <div className="bg-white p-3 rounded-lg">
                <SignaturePad value={signature} onChange={setSignature} width={600} height={180} testid="kiosk-sig" />
              </div>
              <div className="mt-3">
                <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1 block">
                  Selfie {selfieRequired
                    ? <span className="text-red-400">(obligatorio · {selfieReason})</span>
                    : "(opcional — trazabilidad forense)"}
                </label>
                <SelfieCapture value={selfie} onChange={setSelfie} testid="kiosk-selfie" />
              </div>
              <Button
                onClick={sign} disabled={busy || !signature || (selfieRequired && !selfie)}
                data-testid="kiosk-confirm"
                className="mt-4 w-full h-16 text-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black gap-2"
              >
                <CheckCircle size={22} weight="fill" /> {busy ? "Firmando…" : `Confirmar asistencia de ${picked.name.split(" ")[0]}`}
              </Button>
            </>
          )}
        </div>

        {/* Recent signers */}
        {recent.length > 0 && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4" data-testid="kiosk-recent">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              Últimos firmados esta sesión
            </p>
            <ul className="space-y-1 text-sm">
              {recent.map((r, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-300">
                  <ShieldCheck size={14} weight="fill" className={r.offline ? "text-amber-400 shrink-0" : "text-emerald-500 shrink-0"} />
                  <span className="flex-1 truncate">{r.name}</span>
                  {r.offline && <span className="text-[9px] uppercase font-bold text-amber-300 bg-amber-900/40 border border-amber-700 rounded px-1.5 py-0.5">pendiente</span>}
                  <span className="text-[10px] text-slate-500 font-mono">{r.at}</span>
                  <span className="text-[10px] text-slate-500 font-mono">{r.hash}…</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
